import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for saved user on mount
    const savedUser = localStorage.getItem('raath_dine_user');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);

        // ✅ FIX: Ensure shopId is set and synced with cloudSync
        const isInvalidShopId = (id) => !id || id === 'shop_default' || id === 'shop_admin_default' || id === 'null' || id === 'undefined';
        if (parsed.shopId && !isInvalidShopId(parsed.shopId)) {
          localStorage.setItem('raath_shop_id', parsed.shopId);
        } else if (!localStorage.getItem('raath_shop_id') || isInvalidShopId(localStorage.getItem('raath_shop_id'))) {
          // If no valid shopId anywhere, generate new one
          const newShopId = `shop_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem('raath_shop_id', newShopId);
          parsed.shopId = newShopId;
        }

        setUser(parsed);
      } catch (e) {
        localStorage.removeItem('raath_dine_user');
      }
    }
    setLoading(false);
  }, []);

  // Login function - expects userData with role, pages, permissions
  const login = (userData) => {
    // ✅ FIX: Ensure shopId is present and valid
    let shopId = userData.shopId;

    // Helper to check invalid shopIds
    const isInvalidShopId = (id) => !id || id === 'shop_default' || id === 'shop_admin_default' || id === 'null' || id === 'undefined';

    // If invalid shopId, try to get from localStorage or generate new
    if (isInvalidShopId(shopId)) {
      const savedShopId = localStorage.getItem('raath_shop_id');
      if (savedShopId && !isInvalidShopId(savedShopId)) {
        shopId = savedShopId;
        console.log(`[Auth] Using saved shopId: ${shopId}`);
      } else {
        // Generate new shopId if none exists
        shopId = `shop_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        console.log(`[Auth] Generated new shopId: ${shopId}`);
      }
    }

    // Save shopId to localStorage for cloudSync
    localStorage.setItem('raath_shop_id', shopId);

    const userWithRole = {
      ...userData,
      shopId,  // ✅ Ensure shopId is always set
      pages: userData.pages || getDefaultPages(userData.role),
      permissions: userData.permissions || getDefaultPermissions(userData.role),
    };

    setUser(userWithRole);
    localStorage.setItem('raath_dine_user', JSON.stringify(userWithRole));
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('raath_dine_user');
    // ✅ FIX: Don't remove shopId on logout - keep it for next login
    // localStorage.removeItem('raath_shop_id');
    setUser(null);
  };

  // Get default pages based on role
  const getDefaultPages = (role) => {
    const pageMap = {
      admin: ['dashboard', 'pos', 'tables', 'menu', 'orders', 'kitchen', 'inventory', 'accounts', 'reports', 'staff', 'settings', 'backup'],
      manager: ['dashboard', 'pos', 'tables', 'menu', 'orders', 'kitchen', 'inventory', 'accounts', 'reports', 'staff', 'settings'],
      cashier: ['dashboard', 'pos', 'orders', 'tables'],
      waiter: ['dashboard', 'pos', 'tables', 'kitchen', 'orders'],
      kitchen: ['dashboard', 'kitchen', 'orders'],
    };
    return pageMap[role] || ['dashboard'];
  };

  // Get default permissions based on role
  const getDefaultPermissions = (role) => {
    const permMap = {
      admin: ['create_order', 'edit_order', 'delete_order', 'cancel_bill', 'apply_discount', 'split_bill', 'merge_tables', 'refund', 'manage_menu', 'manage_inventory', 'manage_staff', 'view_reports', 'manage_settings', 'manage_backup', 'view_accounts', 'manage_expenses', 'print_bill', 'print_kot', 'update_kitchen_status', 'view_kot'],
      manager: ['create_order', 'edit_order', 'cancel_bill', 'apply_discount', 'split_bill', 'merge_tables', 'refund', 'manage_menu', 'manage_inventory', 'manage_staff', 'view_reports', 'view_accounts', 'manage_expenses', 'print_bill', 'print_kot', 'update_kitchen_status', 'view_kot'],
      cashier: ['create_order', 'edit_order', 'apply_discount', 'split_bill', 'print_bill', 'print_kot'],
      waiter: ['create_order', 'edit_order', 'print_kot', 'view_kot'],
      kitchen: ['update_kitchen_status', 'view_kot'],
    };
    return permMap[role] || [];
  };

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.permissions?.includes(permission) || false;
  };

  // Check if user can access a specific page
  const canAccessPage = (page) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.pages?.includes(page) || false;
  };

  // Get user's role color
  const getRoleColor = () => {
    const colors = {
      admin: '#d32f2f',
      manager: '#1976d2',
      cashier: '#388e3c',
      waiter: '#f57c00',
      kitchen: '#7b1fa2'
    };
    return colors[user?.role] || '#757575';
  };

  // Get user's role label
  const getRoleLabel = () => {
    const labels = {
      admin: 'Administrator',
      manager: 'Manager',
      cashier: 'Cashier',
      waiter: 'Waiter',
      kitchen: 'Kitchen Staff'
    };
    return labels[user?.role] || user?.role || 'User';
  };

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  // Check if user is manager or above
  const isManager = user?.role === 'admin' || user?.role === 'manager';

  const value = {
    user,
    login,
    logout,
    loading,
    hasPermission,
    canAccessPage,
    getRoleColor,
    getRoleLabel,
    isAuthenticated: !!user,
    isAdmin,
    isManager,
    role: user?.role || null,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;